import React, { FC, useEffect, useRef, useState } from "react";
import { Alert, Button, Spin } from "antd";

import Modal from "@components/Modal";

import {
  ErrorServices,
  useDownloadCompanyFunctionsMappingAiSuggestionsMutation,
  useStatusCompanyFunctionsMappingAiSuggestionsMutation,
} from "@/services";

interface Props {
  isVisible: boolean;
  setIsVisible: (visible: boolean) => void;
  companyId: number;
}

const DownloadAiMappings: FC<Props> = ({
  isVisible,
  setIsVisible,
  companyId,
}) => {
  const [checkingAiStatus, setCheckingAiStatus] = useState(false);

  const [aiStatusData, setAiStatusData] = useState<any>(null);

  const pollingRef = useRef<NodeJS.Timeout | null>(null);

  const [generateAiMappingsApi, { isLoading: generatingAiMappings }] =
    useDownloadCompanyFunctionsMappingAiSuggestionsMutation();

  const [getAiMappingsStatusApi] =
    useStatusCompanyFunctionsMappingAiSuggestionsMutation();

  const stopPolling = () => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
  };

  const startPolling = () => {
    stopPolling();

    pollingRef.current = setInterval(async () => {
      try {
        const res = await getAiMappingsStatusApi({
          company_id: companyId,
        }).unwrap();

        setAiStatusData(res?.data);

        const status = res?.data?.status;

        if (status === "completed" || status === "error") {
          stopPolling();
        }
      } catch (error) {
        stopPolling();
      }
    }, 3000);
  };

  const fetchAiMappingStatus = async () => {
    if (!companyId) return;

    try {
      setCheckingAiStatus(true);

      const res = await getAiMappingsStatusApi({
        company_id: companyId,
      }).unwrap();

      setAiStatusData(res?.data);

      const status = res?.data?.status;

      if (status === "processing") {
        startPolling();
      } else {
        stopPolling();
      }
    } catch (error: any) {
      ErrorServices(error);
    } finally {
      setCheckingAiStatus(false);
    }
  };

  const generateAiMappings = async () => {
    if (!companyId) return;

    try {
      // instant UI feedback
      setAiStatusData({
        status: "processing",
      });

      const res = await generateAiMappingsApi({
        company_id: companyId,
      }).unwrap();

      setAiStatusData(res?.data);

      // always start polling after successful POST
      startPolling();
    } catch (error: any) {
      setAiStatusData({
        status: "error",
      });

      ErrorServices(error);
    }
  };

  const downloadExistingFile = async () => {
    if (!aiStatusData?.file_url) return;

    try {
      const link = document.createElement("a");

      link.href = aiStatusData.file_url;

      link.setAttribute(
        "download",
        `company_job_function_mapping_ai_suggestions_${companyId}.csv`
      );

      document.body.appendChild(link);

      link.click();

      document.body.removeChild(link);
    } catch (error) {
      console.error(error);
    }
  };

  const closeModal = () => {
    stopPolling();
    setIsVisible(false);
  };

  useEffect(() => {
    if (isVisible) {
      fetchAiMappingStatus();
    }

    return () => {
      stopPolling();
    };
  }, [isVisible]);

  return (
    <Modal
      width={544}
      onClose={closeModal}
      footer={[
        <Button key="cancel" onClick={closeModal}>
          Cancel
        </Button>,
      ]}
      title="AI Mapping Export"
      isVisible={isVisible}
    >
      {checkingAiStatus ? (
        <div className="flex flex-col items-center justify-center py-10 gap-3">
          <Spin size="large" />

          <div>Checking AI mapping status...</div>
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          {aiStatusData?.status === "processing" && (
            <div className="flex flex-col items-center py-8 gap-4">
              <Spin size="large" />

              <div className="text-center">
                <div className="font-semibold text-lg">
                  Generating AI Mappings
                </div>

                <div className="text-gray-500 capitalize mt-2">
                  Status: {aiStatusData?.status}
                </div>
              </div>
            </div>
          )}

          {aiStatusData?.status === "not_generated" && (
            <>
              <Alert
                type="info"
                message="AI mappings have not been generated yet."
              />

              <Button
                type="primary"
                onClick={generateAiMappings}
                loading={generatingAiMappings}
              >
                Generate AI Mappings
              </Button>
            </>
          )}

          {aiStatusData?.status === "completed" && (
            <>
              {!aiStatusData?.is_up_to_date && (
                <Alert
                  type="warning"
                  message="Existing AI mappings are outdated. You can download the existing file or generate a new one."
                />
              )}

              <div className="text-sm text-gray-500">
                Generated At:
                {aiStatusData?.completed_at
                  ? ` ${new Date(aiStatusData.completed_at).toLocaleString()}`
                  : "-"}
              </div>

              <div className="flex gap-2">
                <Button type="primary" onClick={downloadExistingFile}>
                  {!aiStatusData?.is_up_to_date
                    ? "Download Existing"
                    : "Download"}
                </Button>

                {!aiStatusData?.is_up_to_date && (
                  <Button
                    onClick={generateAiMappings}
                    loading={generatingAiMappings}
                  >
                    Generate New
                  </Button>
                )}
              </div>
            </>
          )}

          {aiStatusData?.status === "error" && (
            <>
              <Alert
                type="error"
                message={
                  aiStatusData?.error_message ||
                  "Failed to generate AI mappings."
                }
              />

              <Button
                type="primary"
                onClick={generateAiMappings}
                loading={generatingAiMappings}
              >
                Retry Generate
              </Button>
            </>
          )}
        </div>
      )}
    </Modal>
  );
};

export default DownloadAiMappings;
